import classNames from 'classnames';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { forwardRef, useCallback, useEffect, useRef } from 'react';
import Slider from 'react-slick';

import SSRAwareLazyLoad from '../SSRAwareLazyLoad';
import SliderNextArrow from './SliderNextArrow';
import SliderPreviousArrow from './SliderPreviousArrow';

const CommonSlider = forwardRef((props, ref) => {
  const {
    className,
    dots,
    lazyLoad,
    lazyLoadOptions,
    children,
    pauseOnHover,
    prevArrow: PrevArrow = SliderPreviousArrow,
    nextArrow: NextArrow = SliderNextArrow,
    arrows,
    infinite,
    autoplay,
    magnified,
    isThumbnailSlider,
    slides,
    centerMode,
    variableWidth,
    onMount,
    ...rest
  } = props;

  const sliderRef = useRef(null);

  /**
   * Manually bring component's ref up to the parent on mount.
   *
   * Usually forwardRef is meant for purposes when you want to expose
   * child components instance to the parent function but if you use forwardRef's ref and store it somewhere
   * up in the component tree, depending on your use case ref might not be available when you need it.
   * Note that parent doesn't detect the change in ref unless using a side-effect, eg. specifically provided
   * using the onMount-function.
   * It's just how React works.
   */
  useEffect((() => onMount && onMount(sliderRef)), []);

  const getVariableWidth = () => {
    if (getSlides().length === 1 && centerMode) {
      return false;
    }

    return variableWidth;
  };

  const getSlides = useCallback(() => {
    if (slides) return slides;

    return children;
  }, [slides, children]);

  const slider = (
    <Slider
      ref={(innerRef) => {
        sliderRef.current = {
          ...innerRef,
          slideCount: getSlides().length,
        };

        if (ref) {
          ref.current = innerRef;
        }
      }}
      pauseOnHover={pauseOnHover || true}
      infinite={infinite || false}
      nextArrow={
        <NextArrow
          elementClassName={className ? `${className}__arrow` : ''}
          infinite={infinite}
          parentRef={sliderRef}
          isThumbnailSlider={isThumbnailSlider}
        />
      }
      prevArrow={
        <PrevArrow
          elementClassName={className ? `${className}__arrow` : ''}
          infinite={infinite}
          parentRef={sliderRef}
          isThumbnailSlider={isThumbnailSlider}
        />
      }
      arrows={(arrows && getSlides().length > 1) || false}
      autoplay={(autoplay && getSlides().length > 1) || false}
      centerMode={
        (getVariableWidth() && centerMode && getSlides().length > 1) || false
      }
      variableWidth={getVariableWidth()}
      dots={(dots && getSlides().length > 1) || false}
      {...rest}
    >
      {getSlides()}
    </Slider>
  );

  const renderSlider = () => {
    /* TODO NNO-424 This seems to have no effect. Products are always loaded. Consider removal. */
    if (lazyLoad) {
      const options = {
        once: true,
        offset: 10,
        ...lazyLoadOptions,
      };

      return <SSRAwareLazyLoad {...options}>{slider}</SSRAwareLazyLoad>;
    } else {
      return slider;
    }
  };

  return (
    <div
      className={classNames(
        'Slider',
        {
          'Slider--with-dots': dots,
          'Slider--full-width': getSlides().length === 1 && !centerMode,
          'Slider--magnified': magnified,
        },
        className
      )}
    >
      {renderSlider()}
    </div>
  );
});

CommonSlider.propTypes = {
  className: PropTypes.string,
  arrows: PropTypes.bool,
  autoplay: PropTypes.bool,
  centerMode: PropTypes.bool,
  dots: PropTypes.bool,
  infinite: PropTypes.bool,
  isThumbnailSlider: PropTypes.bool,
  lazyLoad: PropTypes.bool,
  magnified: PropTypes.bool,
  pauseOnHover: PropTypes.bool,
  variableWidth: PropTypes.bool,
  slides: PropTypes.array,
  lazyLoadOptions: PropTypes.object,
  nextArrow: PropTypes.object,
  prevArrow: PropTypes.object,
  onMount: PropTypes.func,
};

export default observer(CommonSlider);
